import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Upload, Loader2, ShieldCheck, FileText } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const DocRow = ({ d, i, busy, onUpload }) => (
  <div data-testid={`pub-doc-${i}`} className={`flex items-center gap-3 rounded-xl border px-4 py-3.5 transition-colors ${d.received ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-white"}`}>
    {d.received ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /> : <FileText className="h-5 w-5 text-slate-400 shrink-0" />}
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium">{d.name}</p>
      {d.files > 0 && <p className="text-[11px] text-emerald-700">{d.files} file caricati</p>}
    </div>
    <label data-testid={`pub-doc-upload-${i}`} className={`cursor-pointer flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-medium transition-colors ${d.received ? "border border-slate-300 bg-white hover:border-sky-500" : "bg-[#0F172A] text-white hover:bg-sky-600"}`}>
      {busy === i ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}{d.received ? "Aggiungi" : "Carica"}
      <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif" className="hidden" onChange={(e) => { onUpload(i, e.target.files?.[0]); e.target.value = ""; }} />
    </label>
  </div>
);

export default function PublicDocuments() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = () => api.get(`/public/documents/${token}`).then((r) => setInfo(r.data)).catch(() => setError(true));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (i, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("File troppo grande (massimo 10 MB)");
    setBusy(i);
    const fd = new FormData();
    fd.append("file", file);
    if (i !== null) fd.append("doc_index", i);
    try { await api.post(`/public/documents/${token}/upload`, fd); toast.success("Documento caricato, grazie!"); await load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  if (error) return <div className="min-h-screen grid place-items-center p-6 text-center" data-testid="pub-docs-invalid"><p className="text-slate-600">Link non valido o scaduto. Contatta il tuo consulente.</p></div>;
  if (!info) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>;
  const done = info.documents.filter((d) => d.received).length;

  return (
    <div className="min-h-screen bg-[#F8FAFC] grain" data-testid="public-documents-page">
      <header className="bg-[#0F172A] text-white">
        <div className="max-w-2xl mx-auto px-5 py-10 flex items-center gap-4">
          {info.picture ? <img src={info.picture} alt="" className="h-14 w-14 rounded-full border-2 border-white/20" /> : <div className="h-14 w-14 rounded-full bg-sky-600" />}
          <div>
            <p className="text-sky-300 text-xs uppercase tracking-[0.2em]">Documenti · {info.product}</p>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight mt-1">Ciao {info.client_first_name}, carica qui i tuoi documenti</h1>
            <p className="text-slate-300 text-sm mt-1">Li riceverà direttamente {info.agent_name}</p>
          </div>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-5 py-8 space-y-5">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex justify-between text-sm mb-2"><span className="font-medium">Avanzamento</span><span data-testid="pub-docs-progress" className="font-mono">{done}/{info.documents.length}</span></div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${info.documents.length ? (done / info.documents.length) * 100 : 0}%` }} /></div>
          {done === info.documents.length && info.documents.length > 0 && <p data-testid="pub-docs-complete" className="text-sm text-emerald-700 mt-3 font-medium">Perfetto, hai caricato tutti i documenti richiesti!</p>}
        </div>
        <div className="space-y-2.5">{info.documents.map((d, i) => <DocRow key={i} d={d} i={i} busy={busy} onUpload={upload} />)}</div>
        <label data-testid="pub-doc-upload-extra" className="cursor-pointer flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 py-4 text-sm text-slate-600 hover:border-sky-400 transition-colors">
          {busy === null ? <Upload className="h-4 w-4" /> : null}Carica un altro documento{info.extra_files ? ` (${info.extra_files} caricati)` : ""}
          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif" className="hidden" onChange={(e) => { upload(null, e.target.files?.[0]); e.target.value = ""; }} />
        </label>
        <p className="text-xs text-slate-400 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" />Formati accettati: PDF, JPG, PNG, HEIC (max 10 MB). I file sono visibili solo al tuo consulente.</p>
      </main>
    </div>
  );
}
